import type { FailedRefundTransaction } from '~/api/graphql/generated/types'
import i18n from '~/locales/i18n'

import { TransactionFailureReasonName } from '../../../add-payment-dialog-v2/utils/paymentErrorMapping'

/**
 * Error categories for refund transactions
 * Can evolve independently from payment error categories
 */
export enum RefundErrorCategory {
  CONNECTIVITY = 'connectivity',
  ISSUER_DECLINE = 'issuer_decline',
  USER_CANCEL = 'user_cancel',
  CONNECTIVITY_DOWN = 'connectivity_down',
  UNKNOWN = 'unknown',
}

/**
 * Maps transaction failure reason name to refund error category
 * Uses the same failure reason names as payments but can have different category mappings in the future
 */
const FAILURE_REASON_TO_REFUND_CATEGORY_MAP: Record<
  TransactionFailureReasonName,
  RefundErrorCategory
> = {
  [TransactionFailureReasonName.DEVICE_OFFLINE]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_NOT_FOUND]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_TIMEOUT]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_BUSY]: RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_BATTERY_LOW]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.CANCELLED_BY_DEVICE]:
    RefundErrorCategory.USER_CANCEL,
  [TransactionFailureReasonName.CANCELLED_BY_API]:
    RefundErrorCategory.USER_CANCEL,
  [TransactionFailureReasonName.GATEWAY_REJECTED]:
    RefundErrorCategory.CONNECTIVITY_DOWN,
  [TransactionFailureReasonName.PROCESSOR_DECLINED]:
    RefundErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonName.SETTLEMENT_DECLINED]:
    RefundErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonName.UNKNOWN]: RefundErrorCategory.UNKNOWN,
  [TransactionFailureReasonName.TRANSACTION_NOT_SETTLED]:
    RefundErrorCategory.UNKNOWN,
}

const getRefundFailureReasonCategory = (
  failureReasonName: string,
): RefundErrorCategory =>
  FAILURE_REASON_TO_REFUND_CATEGORY_MAP[
    failureReasonName as TransactionFailureReasonName
  ] ?? RefundErrorCategory.UNKNOWN

/**
 * Gets translated message for refund error category
 * Translations are computed dynamically, so they update when i18n language changes.
 */
const getRefundCategoryTranslationMessage = (
  category: RefundErrorCategory,
  linked?: boolean,
): string => {
  switch (category) {
    case RefundErrorCategory.ISSUER_DECLINE:
      return linked
        ? i18n.t(
            'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE_LINKED',
          )
        : i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE')
    case RefundErrorCategory.CONNECTIVITY:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY')
    case RefundErrorCategory.UNKNOWN:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
    case RefundErrorCategory.USER_CANCEL:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_USER_CANCEL')
    case RefundErrorCategory.CONNECTIVITY_DOWN:
      return i18n.t(
        'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_DOWN',
      )
    default:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
  }
}

const getRefundTransactionFailureTranslationMessage = (
  failureReasonName: string,
  linked?: boolean,
): string => {
  const category = getRefundFailureReasonCategory(failureReasonName)

  try {
    return getRefundCategoryTranslationMessage(category, linked)
  } catch {
    // Fallback to unknown message if translation fails
    return getRefundCategoryTranslationMessage(RefundErrorCategory.UNKNOWN)
  }
}

/**
 * Extracts failure reason name from FailedRefundTransaction
 * Uses the name property which matches the backend TransactionFailureReason enum
 */
export const extractRefundFailureReasonName = (
  transaction: FailedRefundTransaction,
): string | null => {
  const failureReason = transaction.error?.failureReason

  if (!failureReason?.name) {
    return null
  }

  return failureReason.name
}

/**
 * Maps a FailedRefundTransaction to a user-friendly error message
 */
export const getRefundTransactionFailureMessage = (
  transaction: FailedRefundTransaction,
  linked?: boolean,
): string => {
  const failureReasonName = extractRefundFailureReasonName(transaction)

  if (failureReasonName === null) {
    // Fallback to generic message if we can't determine failure reason
    return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
  }

  return getRefundTransactionFailureTranslationMessage(
    failureReasonName,
    linked,
  )
}

/**
 * Extracts backend error message from FailedRefundTransaction for error doggo
 * Returns backend message if available, otherwise returns null
 */
export const getRefundTransactionFailureBackendMessage = (
  transaction: FailedRefundTransaction,
): string | null => transaction.error?.message ?? null

/**
 * Maps refund initiation failure to user-friendly error message
 */
export const getRefundInitiationFailureMessage = (): string =>
  i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE')
